import { useMemo } from 'react'
import { EngineAnswer } from '@/components/EngineAnswer'
import { PanelSwitch } from '@/components/PanelSwitch'
import { RivalsBoard } from '@/components/RivalsBoard'
import { SegmentedIndicator } from '@/components/SegmentedIndicator'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { spaceLeaderboard } from '@/lib/clusters'
import type { IntentCluster, Run } from '@/lib/types'
import { useTabDirection } from '@/lib/useTabDirection'

const TABS = ['queries', 'rivals'] as const

const TAB_LABEL: Record<(typeof TABS)[number], string> = {
  queries: 'Queries',
  rivals: 'Rivals',
}

// SpaceDetail is the expanded content for one space, shown INLINE beneath its
// row (no second titled box). It mirrors the parent tab structure with its own
// nested tabs: who wins the space (Rivals) and the underlying answers (Queries).
export function SpaceDetail({
  run,
  cluster,
}: {
  run: Run
  cluster: IntentCluster
}) {
  const tabs = useTabDirection(TABS, 'queries')

  const leaderboard = useMemo(
    () => spaceLeaderboard(cluster.id, run.perQuery, run.brand),
    [cluster.id, run.perQuery, run.brand]
  )

  const queries = useMemo(
    () => run.perQuery.filter((q) => q.clusterId === cluster.id),
    [run.perQuery, cluster.id]
  )

  const competitorNames = useMemo(
    () => run.competitors.map((c) => c.name),
    [run.competitors]
  )

  return (
    // Flush inner section: no own border/margin — the parent <li> wraps both the
    // row and this, so they read as one connected box.
    <div className="border-t border-border bg-muted/20 px-4 pb-4 pt-4">
      {/* Nested tabs — same idea as the top-level lens tabs. */}
      <div role="tablist" className="mb-4 flex gap-1 border-b border-border">
        {TABS.map((id) => {
          const active = id === tabs.active

          return (
            <button
              key={id}
              id={`space-tab-${cluster.id}-${id}`}
              type="button"
              role="tab"
              aria-selected={active}
              aria-controls={`space-panel-${cluster.id}`}
              onClick={() => tabs.select(id)}
              className={`relative -mb-px cursor-pointer px-3 py-2 text-sm font-medium transition-colors ${
                active
                  ? 'text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {active && (
                <SegmentedIndicator
                  layoutId={`space-tab-${cluster.id}`}
                  className="absolute inset-x-0 bottom-0 h-0.5 bg-primary"
                />
              )}
              {TAB_LABEL[id]}
            </button>
          )
        })}
      </div>

      <div
        role="tabpanel"
        id={`space-panel-${cluster.id}`}
        aria-labelledby={`space-tab-${cluster.id}-${tabs.active}`}
      >
        <PanelSwitch
          panelKey={tabs.active}
          index={tabs.index}
          previousIndex={tabs.previousIndex}
        >
          {tabs.active === 'rivals' ? (
            <RivalsBoard board={leaderboard} />
          ) : (
            <Accordion
              type="single"
              collapsible
              className="flex flex-col gap-2"
            >
              {queries.map((q, qi) => (
                <AccordionItem
                  key={`q-${qi}`}
                  value={`q-${qi}`}
                  className="overflow-hidden rounded-lg border border-border bg-card px-4"
                >
                  <AccordionTrigger className="text-sm font-semibold hover:no-underline">
                    <span className="flex w-full items-center gap-2 pr-2 text-left">
                      <span className="shrink-0 text-muted-foreground tabular-nums">
                        Q{qi + 1}
                      </span>
                      <span className="min-w-0 flex-1">{q.query}</span>
                      <span className="shrink-0 text-xs font-normal text-muted-foreground tabular-nums">
                        {q.responses.length} answers
                      </span>
                    </span>
                  </AccordionTrigger>

                  <AccordionContent className="pt-1">
                    <Accordion
                      type="multiple"
                      defaultValue={q.responses
                        .slice(0, 1)
                        .map((r) => r.engineId)}
                      className="divide-y divide-border/60 border-t border-border"
                    >
                      {q.responses.map((r) => (
                        <EngineAnswer
                          key={r.engineId}
                          response={r}
                          brand={run.brand}
                          competitors={competitorNames}
                        />
                      ))}
                    </Accordion>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </PanelSwitch>
      </div>
    </div>
  )
}
